import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AppConfigState } from '@nibtravel/shared/util/app-config';
import {
  IFeatureSummaryList,
  IInsuranceContractSummary,
  ITenderType,
  IUnderwriterSummary,
  IUnderwriterSummaryList,
} from '@nibtravel/shared/util/models';
import { ProductsApiEndpointService } from '@nibtravel/shared/util/url';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  ContractsMap,
  IUnderwriterState,
  PlanToContractMap,
  UnderwritersMap,
} from './models';

@Injectable()
export class UnderwriterDataService {
  constructor(
    private store: Store,
    private http: HttpClient,
    private endPointService: ProductsApiEndpointService
  ) {}

  /** Loads the underwriter summary from products API */
  getUnderwriterSummary(): Observable<Partial<IUnderwriterState>> {
    const brand = this.store.selectSnapshot(AppConfigState.getBrand);
    const url = this.endPointService.getUnderwriterConfigUrl(brand);
    return this.http
      .get<IUnderwriterSummaryList>(url)
      .pipe(map((data) => this.mapUnderwriterData(data)));
  }

  /** Abstracts the creation of map object for readability */
  mapUnderwriterData(
    data: IUnderwriterSummaryList
  ): Partial<IUnderwriterState> {
    const { contractsMap, contractContentMap, planToContractMap } =
      this.mapFromContracts(data.underwriters);

    return {
      underwritersMap: this.underwritersMap(data.underwriters),
      contractsMap: contractsMap,
      contractContentMap: contractContentMap,
      planToContractMap: planToContractMap,
    };
  }

  /**
   * Since the Insurance Contract Id's are unique across the system
   * Mapping the contract by contract id instead of multiple levels of mapping
   *
   * NOTE: a,c are standard reduce variables where
   * a is the accumulated value and c is the current value on the iteration.
   */
  private mapFromContracts(
    underwriters: IUnderwriterSummary[]
  ): Partial<IUnderwriterState> {
    // This flattens to a single array of contracts
    const contracts = underwriters.reduce(
      (a, c) => [...a, ...c.insuranceContracts],
      [] as IInsuranceContractSummary[]
    );

    // Next we create the map with contract id as a map key
    const contractsMap = contracts.reduce(
      (a, c) => ({ ...a, [c.insuranceContractId]: c }),
      {} as ContractsMap
    );

    // With the flatten contracts we can also map the plans to contracts
    const planToContractMap = contracts.reduce((a, c) => {
      // Plans is an array inside each insurance contract.
      // So the actual end goal is to map planId to contract id
      return {
        ...a,
        ...c.plans.reduce(
          (acc, c) => ({ ...acc, [c.planId]: c.insuranceContractId }),
          {}
        ),
      };
    }, {} as PlanToContractMap);

    // Finally we need a quick access to content items, so we map contract id as key
    // then we map the content item type to its content
    const contractContentMap = contracts.reduce(
      (acc, c) => ({
        ...acc,
        // We also want to access the content by content type
        // So here we are mapping each content item using type as key and content as value
        [c.insuranceContractId]: c.contentItems.reduce(
          (a, c) => ({ ...a, [c.type]: c.content }),
          {}
        ),
      }),
      {}
    );
    return { contractsMap, contractContentMap, planToContractMap };
  }

  /** Mapping for underwriterCode and its summary */
  private underwritersMap(
    underwriters: IUnderwriterSummary[]
  ): UnderwritersMap {
    // a = accumulator, c = current
    return underwriters.reduce(
      (a, c) => ({ ...a, [c.code]: c }),
      {} as UnderwritersMap
    );
  }

  /**
   *
   * @param brandCode string
   * @param icId InsuranceContractId as number
   * @returns Observable<IFeatureSummaryList>
   * @description fetch the features of an Insurance contract
   */
  getInsuranceFeature(
    brandCode: string,
    icId: number
  ): Observable<IFeatureSummaryList> {
    const url = this.endPointService.getInsuranceFeaturesUrl(brandCode, icId);
    return this.http.get<IFeatureSummaryList>(url);
  }

  /**
   *
   * @param brandCode string
   * @param contractId InsuranceContractId as number
   * @returns Observable<ITenderType[]>
   * @description fetch the tendertypes supported by insurance contract
   */
  getTenderTypes(
    contractId: number
  ): Observable<Record<number, ITenderType[]>> {
    const brand = this.store.selectSnapshot(AppConfigState.getBrand);
    const url = this.endPointService.getTenderTypeUrl(brand, contractId);
    return this.http
      .get<ITenderType[]>(url)
      .pipe(map((data) => ({ [contractId]: data })));
  }
}
